import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, ArrowLeft, Tag } from "lucide-react";
import { listBlog, getBlog } from "../lib/api";

const CATEGORIES = [
  "All",
  "Mentoring Matters",
  "Youth Mental Wellness",
  "Drug Prevention Awareness",
  "Parenting and Families",
  "Leadership and Life Skills",
  "Community Impact",
  "Events and Announcements",
];

function BlogIndex() {
  const [cat, setCat] = useState("All");
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["blog", cat],
    queryFn: () => listBlog(cat === "All" ? null : cat),
  });

  return (
    <>
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="blog-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-20 md:py-24">
          <div className="max-w-3xl">
            <span className="overline text-[#F5B90A]">News Center</span>
            <h1 className="mt-4 font-display text-5xl md:text-6xl font-bold leading-[1.02]">
              Stories, updates, and <span className="italic text-[#F5B90A]">lessons from the field.</span>
            </h1>
          </div>
        </div>
      </section>

      <section className="bg-[#F7F3EC] py-8 border-y border-black/5">
        <div className="container-x flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${cat === c ? "bg-[#6B21A8] text-white" : "bg-white text-black/80 border border-black/10"}`}
              data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-white py-16" data-testid="blog-list">
        <div className="container-x">
          {isLoading ? (
            <p className="text-black/60">Loading posts…</p>
          ) : posts.length === 0 ? (
            <div className="rounded-lg border border-dashed border-black/10 p-14 text-center">
              <p className="font-display text-xl">No posts published yet{cat !== "All" ? ` in "${cat}"` : ""}.</p>
              <p className="mt-2 text-sm text-black/60">New stories will appear here once published by an administrator.</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} className="card-editorial p-6 block group" data-testid={`blog-post-${p.slug}`}>
                  {p.cover_image && <img src={p.cover_image} alt={p.title} className="rounded-md aspect-video object-cover w-full" />}
                  <p className="mt-5 overline text-[#6B21A8]">{p.category}</p>
                  <h3 className="mt-2 font-display text-xl font-bold group-hover:text-[#6B21A8]">{p.title}</h3>
                  {p.excerpt && <p className="mt-2 text-sm text-black/70">{p.excerpt}</p>}
                  <p className="mt-4 text-xs text-black/50 flex items-center gap-2">
                    <Calendar className="h-3 w-3" /> {p.published_at ? new Date(p.published_at).toLocaleDateString() : ""}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function BlogDetail({ slug }) {
  const { data: post, isLoading, error } = useQuery({
    queryKey: ["blog-post", slug], queryFn: () => getBlog(slug),
  });

  if (isLoading) return <section className="container-x py-24"><p className="text-black/60">Loading…</p></section>;
  if (error || !post) return (
    <section className="container-x py-24 max-w-2xl">
      <h1 className="font-display text-3xl font-bold">Post not found.</h1>
      <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#6B21A8]"><ArrowLeft className="h-4 w-4" /> All posts</Link>
    </section>
  );

  return (
    <>
      <section className="bg-[#0A0A0A] text-white py-16" data-testid="blog-detail-hero">
        <div className="container-x max-w-3xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#F5B90A]"><ArrowLeft className="h-4 w-4" /> All posts</Link>
          <p className="mt-6 overline text-[#F5B90A]">{post.category}</p>
          <h1 className="mt-3 font-display text-4xl md:text-5xl font-bold leading-tight">{post.title}</h1>
          <p className="mt-4 text-sm text-white/60 flex items-center gap-4">
            <span className="inline-flex items-center gap-2"><Calendar className="h-4 w-4" /> {post.published_at ? new Date(post.published_at).toLocaleDateString() : ""}</span>
            {post.author && <span>· {post.author}</span>}
          </p>
        </div>
      </section>
      <section className="bg-white py-16" data-testid="blog-detail-body">
        <div className="container-x max-w-3xl">
          {post.cover_image && <img src={post.cover_image} alt={post.title} className="w-full rounded-lg mb-10" />}
          <article className="prose prose-lg max-w-none whitespace-pre-wrap text-black/85 leading-relaxed">
            {post.body}
          </article>
        </div>
      </section>
    </>
  );
}

export default function Blog() {
  const { slug } = useParams();
  return slug ? <BlogDetail slug={slug} /> : <BlogIndex />;
}
